import { useEffect, useRef, useState } from 'react';
import { Quote } from 'lucide-react';

/**
 * TextCarousel — Iter-51.55.
 *
 * Separate from the video carousel. Each item is either a screenshot
 * (image_url, hosted via Emergent Object Storage) or a plain quote
 * (quote + author). Auto-advances every 5s. Dot indicators below.
 *
 * `items` shape:
 *   [{ testimonial_id, image_url?, quote?, author?, featured }]
 */
export default function TextCarousel({ items }) {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const active = items?.[idx];

  useEffect(() => {
    if (!items || items.length <= 1) return;
    timerRef.current = setTimeout(() => setIdx((i) => (i + 1) % items.length), 5000);
    return () => clearTimeout(timerRef.current);
  }, [idx, items]);

  if (!items || items.length === 0) return null;
  return (
    <div data-testid="text-carousel" className="w-full">
      <div
        data-testid={`text-carousel-slide-${active.testimonial_id}`}
        className="relative w-full rounded-2xl overflow-hidden shadow-md bg-white/95 backdrop-blur"
      >
        {/* Iter-51.94 — Featured pill top-left. Same styling as the
            VideoCarousel ribbon so the two carousels feel like one
            system. Works over both screenshots and text quotes. */}
        {active.featured && (
          <div
            data-testid={`text-carousel-featured-${active.testimonial_id}`}
            className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 rounded-full bg-amber-400 text-amber-950 text-[11px] font-extrabold uppercase tracking-wider px-2 py-0.5 shadow-lg ring-1 ring-amber-600/40"
          >
            <span aria-hidden="true">★</span> Featured
          </div>
        )}
        {active.image_url ? (
          // Screenshot — Iter-51.62: bumped max-h from 40 (160px) to
          // 96 (384px) so a typical portrait WhatsApp screenshot
          // (9:19.5) shows readably at ~180px wide instead of a
          // squished ~74px column. `object-contain` still preserves
          // aspect ratio for landscape uploads.
          <div className="w-full bg-[#F1F5FF] flex items-center justify-center py-2">
            <img
              src={active.image_url}
              alt={active.quote || 'Parent review screenshot'}
              className="max-h-96 w-auto object-contain rounded-lg"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="px-4 py-3 text-left">
            <Quote size={16} className="text-[#FF6B1A] mb-1" strokeWidth={3}/>
            <p className="text-sm text-[#2B2140] font-semibold leading-snug line-clamp-3">
              {active.quote}
            </p>
            {active.author && (
              <p className="text-xs font-bold text-slate-500 mt-1.5">— {active.author}</p>
            )}
          </div>
        )}
      </div>

      {items.length > 1 && (
        <div data-testid="text-carousel-dots" className="flex justify-center items-center gap-1.5 mt-2">
          {items.map((it, i) => (
            <button
              key={it.testimonial_id}
              data-testid={`text-carousel-dot-${i}`}
              onClick={() => setIdx(i)}
              aria-label={`Show testimonial ${i + 1} of ${items.length}`}
              className={
                'h-1.5 rounded-full transition-all ' +
                (i === idx ? 'w-6 bg-[#5B8DEF]' : 'w-1.5 bg-slate-300')
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
